import { loadStripe } from "@stripe/stripe-js";
import { startCase } from "lodash";
import axios, { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { FcGoogle } from "react-icons/fc";
import { FaApple } from "react-icons/fa";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import bgImg from "../pnai-bg.jpg";
import Logo from "./common/Logo";
import Button from "./common/Button";
import Input from "./common/Input";
import Loading from "./common/Loading";
import { SubscriptionStatusType } from "../db/schema";
import { isEntitledStatus } from "../utilities/subscriptionAccess";
import AppleLogin from "react-apple-login";

// Add these utility functions at the top level
const getPriceId = (searchParams: URLSearchParams) =>
  searchParams.get("priceId");
const getRedirectTo = (searchParams: URLSearchParams) =>
  searchParams.get("redirectto");
const appendSearchParams = (path: string, searchParams: URLSearchParams) =>
  `${path}${searchParams.size ? `?${searchParams.toString()}` : ""}`;

// Format seconds into human-readable time
const formatRetryAfter = (seconds: number): string => {
  if (seconds < 60) {
    return "less than a minute";
  }
  const minutes = Math.ceil(seconds / 60);
  return minutes === 1 ? "1 minute" : `${minutes} minutes`;
};

// Check if email domain has SSO configured
const checkSSOAvailability = async (email: string) => {
  try {
    const response = await axios.post("/api/sso/discover", { email });
    if (response.data.sso_required) {
      // Build absolute URL for SSO redirect
      const ssoUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.sso_url}`;
      window.location.href = ssoUrl;
      return true;
    }
  } catch (error) {
    console.error("SSO discovery failed:", error);
  }
  return false;
};

// Move outside of Login component

function Login() {
  const [email, setEmail] = useState<string>(process.env.REACT_APP_EMAIL || "");
  const [password, setPassword] = useState<string>(
    process.env.REACT_APP_PASSWORD || ""
  );
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [errorState, setErrorState] = useState<boolean>(false);
  const navigate = useNavigate();
  const {
    setIsAuthenticated,
    setUser,
    setReportPreferences,
    refreshSubscriptionStatus,
  } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [isInvitation, setIsInvitation] = useState(false);
  const [department, setDepartment] = useState<string>("");
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [checkingSSO, setCheckingSSO] = useState(false);
  const [invitationExpired, setInvitationExpired] = useState(false);
  const [renewalRequested, setRenewalRequested] = useState(false);
  const [renewalError, setRenewalError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    // Check for invitation parameters
    const emailParam = searchParams.get("email");
    const tokenParam = searchParams.get("token");
    const departmentParam = searchParams.get("department");

    if (emailParam && departmentParam && tokenParam) {
      setIsInvitation(true);
      setEmail(emailParam.trim());
      setDepartment(startCase(departmentParam));
    } else if (emailParam && searchParams.get("sso") === "true") {
      // SSO invitations carry no department, so they aren't treated as an
      // invitation above. Still prefill the address the invite went to.
      setEmail(emailParam.trim());
    }
  }, [searchParams]);

  useEffect(() => {
    // Check authentication status when component mounts
    // Invitations to an SSO agency link to /login?sso=true&token=…&email=….
    // Send the officer straight to their identity provider (auto-provisioning
    // and the pending invitation are resolved in the SAML/OIDC callback).
    // Without this the page sat on a blank email form, and officers clicked
    // "Sign up", which carried the email param over and locked the field.
    const startInvitedSso = async () => {
      const emailParam = searchParams.get("email")?.trim();
      if (searchParams.get("sso") !== "true" || !emailParam) return false;
      if (searchParams.get("error")) return false; // came back from a failed SSO
      return checkSSOAvailability(emailParam);
    };

    const checkAuthStatus = async () => {
      let redirectingToSso = false;
      setLoading(true);
      try {
        const response = await axios.get("/api/auth/user");
        if (response.data.user && !("max_users" in response.data.user)) {
          // Check if it's a regular user (not an agency)
          setIsAuthenticated(true);
          setUser(response.data.user);

          // If there's an invitation token, accept it automatically
          const tokenParam = searchParams.get("token");
          const emailParam = searchParams.get("email");
          const departmentParam = searchParams.get("department");
          if (tokenParam && emailParam && departmentParam) {
            try {
              const inviteResponse = await axios.post(
                "/api/agency/accept-invitation",
                {
                  email: emailParam.trim(),
                  userId: response.data.user.id,
                  token: tokenParam,
                }
              );
              if (inviteResponse.data.success) {
                setUser(inviteResponse.data.user);
                navigate("/cases");
                return;
              }
            } catch (inviteError: any) {
              if (
                inviteError?.response?.status === 410 &&
                inviteError?.response?.data?.code === "EXPIRED"
              ) {
                setInvitationExpired(true);
                setErrorState(true);
                setErrors({
                  overall: "This invitation link has expired.",
                });
              } else {
                setErrorState(true);
                setErrors({
                  overall:
                    inviteError?.response?.data?.error ||
                    "Invalid invitation link.",
                });
              }
              setLoading(false);
              return;
            }
          } else if (tokenParam) {
            setLoading(false);
            return;
          }

          // An MFA-pending session can't read subscription status yet
          // (the MFA guard 403s it), so finish MFA first.
          if (
            response.data.mfaRequired &&
            response.data.mfaVerified === false
          ) {
            navigate(
              response.data.mfaConfigured ? "/mfa-challenge" : "/mfa-setup"
            );
            return;
          }

          // Already signed in: forward into the app, but only through the
          // same paywall check the form submit below runs. AuthContext skips
          // its own check when the app loads on /login, so without this an
          // officer with a lapsed subscription got the full dashboard just
          // by opening /login.
          const currentSubscriptionStatus = await refreshSubscriptionStatus();
          if (!isEntitledStatus(currentSubscriptionStatus)) {
            navigate("/subscription");
            return;
          }

          const redirectTo = getRedirectTo(searchParams);
          if (redirectTo) {
            navigate(`/${redirectTo}`);
          } else {
            navigate("/cases");
          }
        } else {
          setIsAuthenticated(false);
          setUser(null);
          redirectingToSso = await startInvitedSso();
        }
      } catch (error) {
        // User is not authenticated, stay on login page
        setIsAuthenticated(false);
        setUser(null);
        redirectingToSso = await startInvitedSso();
      } finally {
        // Keep the spinner up while the browser leaves for the IdP.
        if (!redirectingToSso) setLoading(false);
      }
    };

    checkAuthStatus();
  }, [
    navigate,
    setIsAuthenticated,
    setUser,
    searchParams,
    refreshSubscriptionStatus,
  ]);

  const validateForm = () => {
    let newErrors: {
      email?: string;
      password?: string;
    } = {};

    // Email validation
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email is invalid";
    }

    // Only validate password if password field is shown
    if (showPasswordField) {
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{12,}$/;

      if (!password) {
        newErrors.password = "Password is required";
      } else if (!passwordRegex.test(password)) {
        newErrors.password =
          "Password must be at least 12 characters long and include a combination of uppercase and lowercase letters, numbers, and symbols";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate email first
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setErrors({ email: !email ? "Email is required" : "Email is invalid" });
      setErrorState(true);
      return;
    }

    setCheckingSSO(true);
    setErrors({});
    setErrorState(false);

    try {
      // Check for SSO first
      const ssoRedirected = await checkSSOAvailability(email);
      if (ssoRedirected) {
        return; // SSO redirect happened, don't continue
      }

      // No SSO found, show password field
      setShowPasswordField(true);
    } catch (error) {
      console.error("Error checking SSO:", error);
      // On error, show password field as fallback
      setShowPasswordField(true);
    } finally {
      setCheckingSSO(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // If password field is not shown yet, handle email submission
    if (!showPasswordField) {
      return handleEmailSubmit(e);
    }

    // Otherwise handle full login
    setNotice("");
    if (validateForm()) {
      setLoading(true);

      // Check for SSO first
      if (await checkSSOAvailability(email)) {
        return; // SSO redirect happened
      }

      try {
        const response = await axios.post("/api/auth/login", {
          email: email.toLowerCase(),
          password,
        });

        // Handle MFA required response — redirect before any other fetches
        if (response.data.mfaRequired) {
          setLoading(false);
          if (response.data.mfaConfigured) {
            navigate("/mfa-challenge");
          } else {
            navigate("/mfa-setup");
          }
          return;
        }

        // Fetch report preferences
        const preferencesResponse = await axios.get(
          "/api/settings/report-preferences"
        );

        // Check if the user is an agency by looking for max_users property
        if ("max_users" in response.data.user) {
          // If it's an agency login, redirect to agency login page
          navigate("/agency/login");
          return;
        }

        setIsAuthenticated(true);
        setUser(response.data.user);
        setReportPreferences(preferencesResponse.data.preferences);

        // Check for agency invitation parameters
        const emailParam = searchParams.get("email");
        const tokenParam = searchParams.get("token");
        const departmentParam = searchParams.get("department");

        if (emailParam && departmentParam && tokenParam) {
          try {
            // Accept the invitation with token validation
            const inviteResponse = await axios.post(
              "/api/agency/accept-invitation",
              {
                email: emailParam.trim(),
                userId: response.data.user.id,
                token: tokenParam,
              }
            );

            if (inviteResponse.data.success) {
              setUser(inviteResponse.data.user);
              navigate("/cases");
              return;
            }
          } catch (inviteError: any) {
            console.error("Error accepting invitation:", inviteError);
            setErrorState(true);
            if (
              inviteError?.response?.status === 410 &&
              inviteError?.response?.data?.code === "EXPIRED"
            ) {
              setInvitationExpired(true);
              setErrors({
                overall: "This invitation link has expired.",
              });
            } else {
              setErrors({
                overall: "Invalid invitation link.",
              });
              // Only log out for non-expired errors
              setIsAuthenticated(false);
              setUser(null);
              await axios.post("/api/auth/logout");
            }
            setLoading(false);
            return;
          }
        }

        // Regular user flow continues here...
        const currentSubscriptionStatus = await refreshSubscriptionStatus();

        // Rest of your existing subscription and redirect logic...
        const redirectTo = getRedirectTo(searchParams);
        const priceId = getPriceId(searchParams);

        // console.log("currentSubscriptionStatus");
        // console.log(currentSubscriptionStatus);

        // Check subscription status using the returned value
        if (
          currentSubscriptionStatus !== SubscriptionStatusType.enum.active &&
          currentSubscriptionStatus !== SubscriptionStatusType.enum.trialing
        ) {
          navigate("/subscription");
          return;
        }

        if (redirectTo) {
          navigate(`/${redirectTo}`);
        } else if (priceId) {
          const stripePromise = loadStripe(
            process.env.REACT_APP_STRIPE_PUBLIC_KEY ||
              "pk_live_51PydONG4bf6K0RLmdfEJzwpzVpDCNNdt0HjC5sn5QokyLJij6TflI306Xoi69SSqHORZ9cPnt3xw2leQx9rrYb0B00nka5yr7n"
          );
          const stripe = await stripePromise;
          if (!stripe) throw new Error("Stripe failed to initialize");
          const checkoutResponse = await axios.post(
            "/api/create-checkout-session",
            { priceId },
            { withCredentials: true }
          );

          // Redirect to checkout
          const result = await stripe.redirectToCheckout({
            sessionId: checkoutResponse.data.sessionId,
          });

          if (result.error) {
            throw new Error(result.error.message);
          }
          return; // Stop here and don't proceed to other redirects
        } else {
          navigate("/cases");
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          setErrorState(true);
          const axiosError = error as AxiosError<{
            error: string;
            code?: string;
            provider?: string;
            message?: string;
            retryAfter?: number;
            isFirstLockout?: boolean;
          }>;
          if (axiosError.response) {
            console.error(axiosError.response.data.error);
            console.log(error);

            // Handle rate limit errors (429)
            if (axiosError.response.status === 429) {
              const { retryAfter, isFirstLockout, message } = axiosError.response.data;
              const timeRemaining = retryAfter
                ? formatRetryAfter(retryAfter)
                : "later";

              // Different messages for first lockout vs subsequent attempts
              const errorMessage = message
                ? `${message} Please try again in ${timeRemaining}.`
                : `Too many login attempts. Please try again in ${timeRemaining}.`;

              setErrors({
                overall: errorMessage,
              });
            } else if (axiosError.response.data.code === "PASSWORD_SETUP_SENT") {
              // SSO account whose department turned SSO off: the server just
              // emailed a set-password link.
              setErrorState(false);
              setNotice(
                "Your department no longer uses single sign-on, so your account needs a password. We've emailed you a link to set one."
              );
            } else if (axiosError.response.data.code === "USE_PROVIDER") {
              const provider =
                axiosError.response.data.provider === "apple" ? "Apple" : "Google";
              setErrors({
                overall: `This account signs in with ${provider}. Use "Continue with ${provider}" below.`,
              });
            } else if (axiosError.response.data.code === "SSO_REQUIRED") {
              if (await checkSSOAvailability(email)) return;
              setErrors({
                overall: "Sign in with your department's single sign-on.",
              });
            } else {
              setErrors({
                overall: "Incorrect login credentials.",
              });
            }
          }
        }
      } finally {
        setLoading(false);
      }
    } else {
      setErrorState(true);
    }
  };

  const handleRenewalRequest = async () => {
    const emailParam = searchParams.get("email");
    const tokenParam = searchParams.get("token");
    if (!emailParam || !tokenParam) return;

    try {
      await axios.post("/api/agency/renew-invitation", {
        email: emailParam.trim(),
        token: tokenParam,
      });
      // Log the user out so the new email link works cleanly
      setIsAuthenticated(false);
      setUser(null);
      await axios.post("/api/auth/logout").catch(() => {});
      setRenewalRequested(true);
      setRenewalError("");
    } catch (error: any) {
      setRenewalError(
        error?.response?.data?.error || "Failed to request new invitation."
      );
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google`;
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2">
      <div
        className="hidden md:flex relative items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bgImg})` }}
      >
        <div className="absolute inset-0 bg-blue-50/70 dark:bg-blue-900/70"></div>
        <div className="w-3/5 z-10">
          <Logo className="w-full h-auto" />
        </div>
      </div>
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-base-200">
        <div className="bg-base-100 p-8 rounded-xl shadow-md w-full max-w-sm">
          {isInvitation ? (
            <>
              <h1 className="text-3xl mb-4 font-semibold">Accept Invitation</h1>
              <p className="mb-3 text-xs text-base-content/60">
                You've been invited to join {department} with your existing
                account. Please login to activate your subscription.
              </p>
            </>
          ) : (
            <h1 className="text-3xl mb-4 font-semibold">Login</h1>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <p className="font-semibold pb-1 text-sm">Email</p>
              <Input
                type="email"
                id="email"
                value={email}
                placeholder="Email address"
                onChange={(e) => {
                  errorState && setErrorState(false);
                  setEmail(e.target.value);
                  // Reset password field when email changes
                  if (showPasswordField) {
                    setShowPasswordField(false);
                    setPassword("");
                  }
                }}
                disabled={isInvitation}
                className={`input w-full ${errorState && "border-red-500"} ${
                  isInvitation && "bg-gray-100 dark:bg-base-200"
                }`}
              />
              {errors.email && errorState && (
                <p className="mt-1 text-xs text-red-500">{errors.email}</p>
              )}
            </div>

            {/* Password field with transition */}
            <div
              className={`transition-all duration-300 ease-in-out overflow-hidden ${
                showPasswordField
                  ? "max-h-32 opacity-100 transform translate-y-0"
                  : "max-h-0 opacity-0 transform -translate-y-2"
              }`}
            >
              <div className="space-y-1">
                <p className="font-semibold pb-1 text-sm">Password</p>
                <Input
                  type="password"
                  id="password"
                  value={password}
                  placeholder="Password"
                  onChange={(e) => {
                    errorState && setErrorState(false);
                    setPassword(e.target.value);
                  }}
                  className={`input w-full max-w-xs ${
                    errorState && "border-red-500"
                  }`}
                />
                {errors.password && errorState && (
                  <p className="mt-1 text-xs text-red-500">{errors.password}</p>
                )}
                {errors.overall && errorState && (
                  <p className="mt-1 text-xs text-red-500">{errors.overall}</p>
                )}
                {notice && (
                  <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                    {notice}
                  </p>
                )}
              </div>
            </div>

            {invitationExpired && (
              <div>
                {!renewalRequested && (
                  <div className="mt-2">
                    <Button
                      className="w-full"
                      variant="outline"
                      type="button"
                      onClick={handleRenewalRequest}
                    >
                      Request New Invitation
                    </Button>
                  </div>
                )}
                {renewalRequested && (
                  <p className="mt-2 text-xs text-green-600 dark:text-green-400">
                    A new invitation has been sent. Please check your email.
                  </p>
                )}
                {renewalError && (
                  <p className="mt-2 text-xs text-red-500">{renewalError}</p>
                )}
              </div>
            )}

            {!invitationExpired && (
              <div>
                <Button
                  className="w-full"
                  variant="primary"
                  type="submit"
                  disabled={checkingSSO}
                >
                  {checkingSSO ? (
                    <span className="flex items-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                      Checking...
                    </span>
                  ) : showPasswordField ? (
                    isInvitation ? (
                      "Accept Invitation"
                    ) : (
                      "Login"
                    )
                  ) : (
                    "Continue"
                  )}
                </Button>
              </div>
            )}
            {!isInvitation && showPasswordField && (
              <div className="flex justify-center !mt-2">
                <p className="text-sm">
                  <Link
                    className="text-base-content/60 text-xs"
                    to="/reset-password/new"
                  >
                    Forgot Password?
                  </Link>
                </p>
              </div>
            )}
          </form>

          {!isInvitation && (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-base-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-base-100 text-base-content/60">Or</span>
                </div>
              </div>

              <Button
                onClick={handleGoogleLogin}
                variant="outline"
                className="w-full mb-2"
              >
                <FcGoogle className="w-5 h-5" />
                <span>Continue with Google</span>
              </Button>
              <AppleLogin
                clientId="ai.app.policenarratives"
                redirectURI="https://app.policenarratives.ai/api/auth/apple/callback"
                scope="name email"
                responseType="code"
                responseMode="query"
                render={(renderProps) => (
                  <Button
                    onClick={renderProps.onClick}
                    variant="outline"
                    className="w-full mb-2"
                    type="button"
                  >
                    <FaApple className="w-5 h-5" />
                    <span>Continue with Apple</span>
                  </Button>
                )}
              />
              <div className="flex justify-center">
                <p className="font-semibold text-sm text-base-content/60">
                  Don't have an account?{" "}
                  <Link to={appendSearchParams("/signup", searchParams)}>
                    Sign up
                  </Link>
                </p>
              </div>
              <div className="flex justify-center mt-2">
                <p className="font-normal text-xs text-base-content/60">
                  Switch to{" "}
                  <Link to={appendSearchParams("/agency/login", searchParams)}>
                    Agency Admin view
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;
